import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { ProfileContactModal } from './ProfileContactModal'
import type { Student } from '../hooks/useAuth'
import { computeDiscoveryCooldown } from '../lib/discoveryCooldown'

/**
 * SubjectsBanners - the two contextual banners on /subjects:
 *
 *   1. WhatsApp opt-in - shown once the student has committed to at least one
 *      real course AND 2 "days" have passed (≈30s in DEMO_MODE).
 *
 *   2. Re-evaluate / explore - two modes:
 *        a) Student on placeholder ("Só quero praticar") OR no programme yet
 *           → ALWAYS visible (modulo dismissal), message is
 *             "Ainda não escolheste um curso?" - inviting commitment.
 *        b) Student has a real course → from day 2+ (≈30s DEMO), message is
 *             "Descobre se tens potencial para outros cursos..." - inviting
 *             exploration of alternatives. Dismiss = "Estou confortável com
 *             as minhas escolhas."
 *
 * Dismissal: 7 days (WhatsApp) / 14 days (re-evaluate) in prod - in DEMO_MODE
 * these become ~105s / 210s via BANNER_TIME_UNIT_MS.
 */

interface Props {
  student: Student
  onChanged: () => void   // caller refetches /auth/me to refresh banner state
  showWhatsappBanner?: boolean
  showReevaluateBanner?: boolean
  showGrade12Reprompt?: boolean
}

export function SubjectsBanners({
  student,
  onChanged,
  showWhatsappBanner = true,
  showReevaluateBanner = true,
  showGrade12Reprompt = true,
}: Props) {
  const navigate = useNavigate()
  const [showProfile, setShowProfile] = useState(false)
  const [busy, setBusy]               = useState(false)
  const days = student.days_since_signup ?? 0

  // Dismiss-until timestamps
  const waDismissed = student.banners?.whatsapp_dismissed_until
  const waNotDismissed = !waDismissed || new Date(waDismissed).getTime() <= Date.now()
  const reDismissed = student.banners?.reevaluate_dismissed_until
  const reNotDismissed = !reDismissed || new Date(reDismissed).getTime() <= Date.now()

  // A "committed" student = has ≥1 real programme (placeholder doesn't count).
  const committed = Boolean(student.has_programme) && student.on_placeholder === false

  // WhatsApp: only after the student commits to a real course + 2 "days" pass
  // (≈30s DEMO_MODE). Respects opt-in state + dismiss cooldown.
  // Hidden while outbound WhatsApp is disabled - opt-in is misleading until
  // ENABLE_WHATSAPP_OUTBOUND flips on. Re-enable by removing the `&& false`.
  const waVisible = committed && days >= 2 && !student.whatsapp_opted_in && waNotDismissed && false

  // Re-evaluate banner - two distinct modes:
  //   - undecided student (placeholder / none) → always visible while not dismissed
  //   - committed student → from day 2+ (≈30s DEMO_MODE)
  const undecided = student.on_placeholder === true || !student.has_programme
  const reVisible = reNotDismissed && (undecided || (committed && days >= 2))

  // Avaliação Inteligente Phase 1a - tier-driven cooldown after a
  // finished placement. We don't hide the banner during cooldown (per
  // request: student should still see the avaliação slot), but we
  // disable the CTA and surface the countdown so the framing is "you
  // already did it - wait X days for fresh signal" instead of "do it
  // again now". Logic lives in lib/discoveryCooldown.ts - same helper
  // is used by ProfilePage so both surfaces stay in lockstep.
  // Undecided students never completed a placement, so neither gate
  // fires for them.
  const cooldown = computeDiscoveryCooldown(student)
  const inCooldown            = !undecided && cooldown.inCooldown
  const retakeForeverDisabled = !undecided && cooldown.retakeForeverDisabled
  const retakeBlocked         = inCooldown || retakeForeverDisabled
  const cooldownDaysRemaining = cooldown.cooldownDaysRemaining
  const daysSincePlacement    = cooldown.daysSincePlacement
  const hasPreviousDiscovery  = !undecided && !!student.last_placement_at

  // 2026-04-29: profile-completion was rendering TWICE on /subjects -
  // once here and once via the ProfileCompletionBanner inside the
  // priority-ordered single-slot stack in SubjectSelectPage.tsx. The
  // single-slot version is the source of truth (covers phone + provincia
  // + dismissal cooldown), so the duplicate here is removed. Leaving
  // the constant as `false` so the rest of the visibility logic + the
  // early-return on line below can stay structurally identical.
  const profileVisible = false

  // Grade-12 re-prompt banner (DUAL_MODE_NATIONAL_12_PLAN.md §3.4).
  // Surfaces on a 15-day cycle for students who answered "Não" / "Outro"
  // to the Grade-12 question, capped at 3 lifetime asks. The point is to
  // catch students transitioning into 12ª (e.g. an 11th-grader who'll be
  // in 12ª next year) without becoming spammy. Three CTAs map to
  // /auth/grade-12-reprompt actions: accepted / declined / snoozed.
  const repromptCount = student.grade_12_reprompt_count ?? 0
  const repromptAfter = student.grade_12_reprompt_after
  const repromptDue   = repromptAfter ? new Date(repromptAfter).getTime() <= Date.now() : false
  const grade12RepromptVisible = repromptDue && repromptCount < 3
  const renderWa = showWhatsappBanner && waVisible
  const renderRe = showReevaluateBanner && reVisible
  const renderGrade12Reprompt = showGrade12Reprompt && grade12RepromptVisible

  const dismiss = async (banner: 'whatsapp' | 'reevaluate') => {
    setBusy(true)
    try {
      await api.post('/auth/dismiss-banner', { banner })
      onChanged()
    } finally { setBusy(false) }
  }

  /** POST to /auth/grade-12-reprompt and refresh the user so the banner
   *  hides immediately. Failures are non-fatal - the next /auth/me will
   *  re-evaluate the schedule. */
  const reprompt = async (action: 'accepted' | 'declined' | 'snoozed') => {
    setBusy(true)
    try {
      await api.post('/auth/grade-12-reprompt', { action })
      onChanged()
    } finally { setBusy(false) }
  }

  if (!renderWa && !renderRe && !profileVisible && !renderGrade12Reprompt) return null

  return (
    <div className="space-y-2 mb-4">
      {/* Profile-completion banner intentionally removed here -
          ProfileCompletionBanner in the priority-stack on
          SubjectSelectPage handles it as a single source. */}

      {/* Grade-12 re-prompt - amber accent so it visually distinguishes
          itself from the indigo (admission) and fuchsia (re-evaluate)
          banners; matches the §3.8 "12ª uses amber" decision. The three
          CTAs cover commitment / refusal / snooze without shaming any
          choice. Wording mirrors §3.4 in the plan. */}
      {renderGrade12Reprompt && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900 mb-0.5">🎓 Estás na 12ª classe agora?</p>
            <p className="text-xs text-amber-700 mb-2">
              Posso preparar-te para o exame nacional também - se estás em 12ª este ano, podemos focar aí.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => reprompt('accepted')}
                disabled={busy}
                className="text-xs bg-amber-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-700 disabled:opacity-40"
              >
                Sim, mostrar 12ª
              </button>
              <button
                onClick={() => reprompt('declined')}
                disabled={busy}
                className="text-xs text-amber-800 hover:text-amber-900 px-3 py-1.5 disabled:opacity-40"
              >
                Ainda não
              </button>
              <button
                onClick={() => reprompt('snoozed')}
                disabled={busy}
                className="text-xs text-amber-800 hover:text-amber-900 px-3 py-1.5 disabled:opacity-40"
              >
                Mais tarde
              </button>
            </div>
          </div>
        </div>
      )}

      {renderWa && (
        <div className="bg-green-50 border border-green-100 rounded-xl p-3 flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-green-900 mb-0.5">Receber 1 questão por dia no WhatsApp?</p>
            <p className="text-xs text-green-700 mb-2">Mantém-te consistente sem ter de abrir a app. Leva 10 segundos a configurar.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowProfile(true)}
                disabled={busy}
                className="text-xs bg-green-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-40"
              >
                Activar WhatsApp
              </button>
              <button
                onClick={() => dismiss('whatsapp')}
                disabled={busy}
                className="text-xs text-green-700 hover:text-green-900 px-2 py-1.5 disabled:opacity-40"
              >
                Agora não
              </button>
            </div>
          </div>
          <button
            onClick={() => dismiss('whatsapp')}
            disabled={busy}
            className="text-green-300 hover:text-green-600 text-xl leading-none disabled:opacity-40"
            title="Dispensar por 7 dias"
          >
            ×
          </button>
        </div>
      )}

      {renderRe && (
        <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-3 flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold text-fuchsia-900 mb-0.5">
              {undecided
                ? 'Ainda não escolheste um curso?'
                : retakeBlocked
                  ? 'Avaliação concluída'
                  : hasPreviousDiscovery
                    ? 'Repetir avaliação inteligente'
                  // Voice Foundation PR follow-up (2026-05-04). "Queres
                  // testar a tua potencialidade?" → "Queres ver onde
                  // podes concorrer?" Matches the RecommendationsPage +
                  // /subjects banner concorrer-vocabulary lock. Drops
                  // "potencialidade" (technical) in favour of the
                  // student-facing question.
                  : 'Queres ver onde podes concorrer?'}
            </p>
            {/* Avaliação Inteligente Phase 1a (D3a, D3b). Committed-student
                copy is rewritten in the honest-promise framing - we don't
                yet compute admission probability, so we promise course
                level alignment, not admission odds. During the 30-day
                cooldown the framing acknowledges completion and explains
                why re-running now won't help.
                Voice Foundation PR follow-up (2026-05-04): "cursos
                compatíveis" → "onde podes concorrer"; "alinham com o
                teu nível" dropped (corporate); "quiçá" replaced with
                companion phrasing. */}
            <p className="text-xs text-fuchsia-700 mb-2">
              {undecided
                ? 'Responde a algumas questões e dizemos-te a que cursos podes concorrer.'
                : retakeForeverDisabled
                  ? 'Já fizeste a Avaliação Inteligente. Vê onde podes concorrer com base nas disciplinas que escolheste.'
                  : inCooldown && cooldownDaysRemaining !== null
                    // Day-countdown lives on the disabled pill below;
                    // prose stays short.
                    ? 'Já fizeste a Avaliação Inteligente - vê os cursos sugeridos. Continua a praticar; vamos ajudar-te a chegar mais longe.'
                    : 'Faz um teste curto nas disciplinas onde te sentes mais confiante. Mostramos os cursos a que podes concorrer com isso.'}
            </p>
            <div className="flex gap-2 flex-wrap">
              {retakeBlocked ? (
                // Two cases:
                //   - Cooldown active (>0 days): show countdown pill so
                //     the student knows when re-take re-opens.
                //   - Tier has repeat_discovery_days = -1: re-take is
                //     permanently disabled. Skip the pill entirely -
                //     just route them to /recommendations.
                <>
                  <button
                    onClick={() => navigate('/recommendations')}
                    disabled={busy}
                    className="text-xs bg-fuchsia-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-fuchsia-700 disabled:opacity-40"
                  >
                    Ver onde podes concorrer
                  </button>
                  {inCooldown && (
                    <span
                      className="text-xs bg-fuchsia-100 text-fuchsia-700 font-semibold px-3 py-1.5 rounded-lg cursor-not-allowed"
                      title={`Em espera - última avaliação há ${daysSincePlacement} ${daysSincePlacement === 1 ? 'dia' : 'dias'}`}
                    >
                      Repetir em {cooldownDaysRemaining} {cooldownDaysRemaining === 1 ? 'dia' : 'dias'}
                    </span>
                  )}
                </>
              ) : (
                <button
                  onClick={() => navigate(undecided ? '/recommendations' : '/discovery/start')}
                  disabled={busy}
                  className="text-xs bg-fuchsia-600 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-fuchsia-700 disabled:opacity-40"
                >
                  {undecided ? 'Começar descoberta' : hasPreviousDiscovery ? 'Repetir avaliação' : 'Começar avaliação'}
                </button>
              )}
              <button
                onClick={() => dismiss('reevaluate')}
                disabled={busy}
                className="text-xs text-fuchsia-700 hover:text-fuchsia-900 px-2 py-1.5 disabled:opacity-40"
              >
                {undecided ? 'Mais tarde' : 'Confortável com as minhas escolhas'}
              </button>
            </div>
          </div>
          <button
            onClick={() => dismiss('reevaluate')}
            disabled={busy}
            className="text-fuchsia-300 hover:text-fuchsia-600 text-xl leading-none disabled:opacity-40"
            title="Dispensar"
          >
            ×
          </button>
        </div>
      )}

      {showProfile && (
        <ProfileContactModal
          initialPhone={student.phone_number}
          initialOptedIn={student.whatsapp_opted_in}
          onClose={() => setShowProfile(false)}
          onSaved={() => { setShowProfile(false); onChanged() }}
        />
      )}
    </div>
  )
}
